import { type ReactNode, useEffect, useState } from "react"

import { Box, CircularProgress, Typography } from '@mui/material'
import { Navigate } from "react-router-dom"

import { authService } from "../api"

type ProtectedRouteProps = {
  children: ReactNode
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const checkAuth = async () => {
      const hasToken = authService.isAuthenticated()

      if (!hasToken) {
        setIsAuthenticated(false)
        setIsLoading(false)
        return
      }

      try {
        // Verify token is valid by making authenticated request
        await authService.getCurrentUser()
        setIsAuthenticated(true)
      } catch {
        // Token is invalid, clear it
        await authService.logout()
        setIsAuthenticated(false)
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [])

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    )
  }

  if (isAuthenticated === false) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}
